import { generateTheme, ThemeSource } from '@app/lib/helpers/generate-theme';
import { getDocByUid } from '@app/lib/prismic/prismicio-client';
import { Language } from '@app/lib/store-context/Context';
import { createClient } from 'prismicio';
import { ProductFeature } from '@app/[lang]/products/product-features/types';

type PrismicFeatureThemeSource = Extract<ThemeSource, 'Light' | 'Dark'>;

type PrismicImage = {
    url?: string | null;
    alt?: string | null;
};

type PrismicProductFeatureItem = {
    desktop_image?: PrismicImage | null;
    mobile_image?: PrismicImage | null;
    description?: string | null;
};

type PrismicSlice = {
    slice_type?: string | null;
    variation?: string | null;
    primary?: {
        context?: string | null;
        background_colour?: string | null;
        desktop_theme?: PrismicFeatureThemeSource | null;
        mobile_theme?: PrismicFeatureThemeSource | null;
    };
    items?: PrismicProductFeatureItem[];
};

export type PrismicProductFeatureDocument = {
    data?: {
        slices?: PrismicSlice[];
    };
};

export type PrismicProductFeatureContent = {
    features: ProductFeature[];
    backgroundColor?: string;
    context: string;
    theme: string;
};

const isProductFeatureSlice = (slice: PrismicSlice): boolean =>
    slice.slice_type === 'pdp_feature_images' && slice.variation === 'default';

export function extractPrismicProductFeatureContent(
    document: PrismicProductFeatureDocument | null
): PrismicProductFeatureContent | null {
    const featureSlice = document?.data?.slices?.find(isProductFeatureSlice);

    if (!featureSlice) return null;

    const features = (featureSlice.items ?? [])
        .map((item): ProductFeature | null => {
            const desktopImage = item.desktop_image?.url;
            if (!desktopImage) return null;

            return {
                type: 'IMAGE',
                image: {
                    desktopImage,
                    mobileImage: item.mobile_image?.url || desktopImage,
                    altText:
                        item.desktop_image?.alt ||
                        item.mobile_image?.alt ||
                        'Product Image',
                },
                description: item.description || '',
            };
        })
        .filter((feature): feature is ProductFeature => feature !== null);

    if (!features.length) return null;

    return {
        features,
        backgroundColor: featureSlice.primary?.background_colour || undefined,
        context: featureSlice.primary?.context || 'Features',
        theme: generateTheme({
            desktopTheme: featureSlice.primary?.desktop_theme || undefined,
            mobileTheme: featureSlice.primary?.mobile_theme || undefined,
        }),
    };
}

export async function fetchPrismicProductFeatureContent(
    uid: string | null,
    lang: Language
): Promise<PrismicProductFeatureContent | null> {
    if (!uid) return null;

    const client = createClient();
    const document = (await getDocByUid(
        'product',
        uid,
        client,
        lang
    )) as PrismicProductFeatureDocument | null;

    return extractPrismicProductFeatureContent(document);
}
